import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppState } from "../contexts/AppContext";
import DashboardWidget from "../components/DashboardWidget";
import { get } from "../helpers/Dashboard";
import dayjs from "dayjs";
import { getInvoices } from "../helpers/Invoices";
import { Link } from "react-router-dom";
import { format } from "../helpers/Currency";
import Pagination from "../components/Pagination";
import { getProducts } from "../helpers/Products";
import { getCategories } from "../helpers/Categories";
import StoreTag from "../components/StoreTag";
import ListHeader from "../components/ListHeader";



export default function Products() {
    const { appState } = useAppState();
    const [loading, setLoading] = useState(false);

    const [products, setProducts] = useState<Array<any>>([])
    const [categories, setCategories] = useState<Array<any>>([])
    const [search, setSearch] = useState<string | null>(null)
    const [categoryId, setCategoryId] = useState<number | null>(null)
    const [page, setPage] = useState<number>(1)
    const [meta, setMeta] = useState<any>({
        page: 1,
        totalPages: 1,
    });
    const abortController = useRef<AbortController>();

    const excludeMarketplace = useRef<boolean>(true);
    const includeAmazon = useRef<boolean>(true);
    const inStock = useRef<boolean>(true);
    const checkAttributes = useRef<boolean>(false);

    const getProductsFn = useCallback(() => {
        setLoading(true);

        if (abortController.current) {
            abortController.current.abort()
        }
        abortController.current = new AbortController();

        const filters: Record<string, any> = {
            search,
            mp: excludeMarketplace.current,
            amazon: includeAmazon.current,
            inStock: inStock.current,
            attributes: checkAttributes.current,
        }

        if (categoryId) {
            filters.category_id = categoryId
        }

        getProducts(50, page, {
            ...filters,
        }, { signal: abortController.current.signal }).then((result) => {
            setProducts(result.data)
            setMeta({
                page: result.current_page,
                totalPages: result.last_page,
            })
            setLoading(false);
        }).catch((error) => {
            if (error.name === 'AbortError') {
                return;
            }

            setLoading(false);
        })
    }, [page, search, categoryId])

    useEffect(() => {
        getProductsFn();
    }, [page, search, getProductsFn])

    useEffect(() => {
        getCategories(100, 1, null, undefined).then((result) => {
            setCategories(result.data)
        });
    }, [])

    const toggleMarketplace = () => {
        excludeMarketplace.current = !excludeMarketplace.current;

        if (page !== 1) {
            setPage(1);
        } else {
            getProductsFn();
        }
    }
    const toggleAmazon = () => {
        includeAmazon.current = !includeAmazon.current;

        if (page !== 1) {
            setPage(1);
        } else {
            getProductsFn();
        }
    }

    const toggleInStock = () => {
        inStock.current = !inStock.current;

        if (page !== 1) {
            setPage(1);
        } else {
            getProductsFn();
        }
    }

    const toggleAttributes = () => {
        checkAttributes.current = !checkAttributes.current;

        if (page !== 1) {
            setPage(1);
        } else {
            getProductsFn();
        }
    }

    const resetFilters = () => {
        setSearch(null);
        excludeMarketplace.current = true;
        includeAmazon.current = true;
        inStock.current = true;
        checkAttributes.current = false;
        setCategoryId(null);
        if (page !== 1) setPage(1); else getProductsFn();
    };

    // useEffect(() => {
    //     setPage(1);
    // }, [search])

    return (
        <>
            <ListHeader
                title="Products"
                searchPlaceholder="Search by name, SKU, brand…"
                onSearch={(v) => {
                    setSearch(v || null);
                    if (page !== 1) setPage(1); else getProductsFn();
                }}
                chips={[
                    { id: 'mp', label: 'Exclude marketplaces', active: excludeMarketplace.current, onToggle: toggleMarketplace },
                    { id: 'amz', label: 'Include Amazon', active: includeAmazon.current, onToggle: toggleAmazon },
                    { id: 'stk', label: 'In stock', active: inStock.current, onToggle: toggleInStock },
                    { id: 'attr', label: 'Without attributes', active: checkAttributes.current, onToggle: toggleAttributes },
                ]}
                categories={categories?.map((c: any) => ({ id: c.id, name: c.name })) || []}
                activeCategoryId={categoryId}
                onSelectCategory={(id) => {
                    setCategoryId(id as number | null);
                    if (page !== 1) setPage(1); else getProductsFn();
                }}
                onReset={resetFilters}
                rightActions={
                    <Link
                        to="/products/create"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
                    >
                        Create Product
                    </Link>
                }
            />
            <div className="mt-3 p-3 rounded bg-white">
                {loading ? (
                    <div className="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible">
                        <svg className="text-blue-300 w-12 h-12 animate-spin" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"
                            width="24" height="24">
                            <path
                                d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                                stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
                            <path
                                d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                                stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" className="text-blue-500">
                            </path>
                        </svg>
                    </div>
                ) : (<table className="w-full">
                    <thead className="text-left">
                        <tr className="border-b-1">
                            <th className="p-2">Product</th>
                            <th className="p-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product: any) => {
                            let hasAmazon = false;

                            return (
                                <tr className="border-b-1 border-gray-300">
                                    <td className="p-2">
                                        <div className="flex items-start gap-x-2 max-w-[800px]">
                                            <div className="w-8">
                                                <img width="32" height='auto' src={product.primaryImage?.url} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium leading-snug">
                                                    <Link to={`/products/${product.id}`} className="text-blue-600 underline">
                                                        {product.name}
                                                    </Link>
                                                </div>
                                                <div className="text-xs text-gray-600 mt-0.5">
                                                    id:{product.id} • SKU: {product.sku || "—"} • Brand: {product.brand?.name || "—"} • Created: {dayjs(product.createdAt).format('MMM D, YYYY')}
                                                </div>
                                                <div className="text-xs text-gray-600 mt-0.5 flex gap-x-2">
                                                    {product.storeProducts.map((storeProduct) => (
                                                        <StoreTag name={storeProduct.store.name} marketplace={storeProduct.marketplace} link={storeProduct.url} />
                                                    ))}
                                                </div>
                                                <div className="text-xs mt-1">
                                                    <a
                                                        className="text-blue-500 underline"
                                                        href={`https://pricehunt.ca/products/${product.alias}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        Public page
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                    </td>
                                    <td className="p-2 ">
                                        <ul className="flex justify-end gap-x-2">
                                            {hasAmazon === false ? (
                                                <li>
                                                    <Link to={`/products/${product.id}/amazon`}>Add amazon</Link>
                                                </li>
                                            ) : null}

                                            <li>
                                                <a href={`https://pricehunt.ca/products/${product.alias}`}>Link</a>
                                            </li>
                                        </ul>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>)}

            </div >
            {!loading && products && products.length > 0 ? (
                <div className="w-full mt-5">
                    <Pagination page={meta.page} totalPages={meta.totalPages} onCallback={setPage} />
                </div>
            ) : null
            }

        </>
    );
}
